import React, { useEffect, useState } from "react";
import GenericModal from "../../Ui/Modal";
import PlusIcon from "../../assets/icons/plus.svg";
import PaymentIllustration from "../../assets/images/ModalLogo.png";
import { useUser } from "../../context/UserContext";
import useApiHandler from "../../hooks/ApiHandler";
import { User as UserApi } from "../../../service.js";
import APPLICATION_SETTINGS from "../../../settings.js";
import { Elements, CardElement, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

// Initialize Stripe once
const stripePromise = loadStripe(APPLICATION_SETTINGS.STRIPE_API_KEY);

// Inner component responsible for Stripe card entry and submission
const CardPaymentForm = ({
  nameOnCard,
  setNameOnCard,
  billingAddress,
  setBillingAddress,
  stateCode,
  setStateCode,
  zip,
  setZip,
  usStates,
  onBack,
  onSavePaymentMethod,
  saving,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit =
    !!stripe &&
    !!elements &&
    cardComplete &&
    nameOnCard.trim().length > 0 &&
    billingAddress.trim().length > 0 &&
    stateCode.trim().length > 0 &&
    zip.trim().length > 0 &&
    !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!canSubmit) return;

    const cardElement = elements.getElement(CardElement);
    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: nameOnCard,
          address: {
            line1: billingAddress,
            state: stateCode,
            postal_code: zip,
          },
        },
      });

      if (pmError) {
        setError(pmError.message || "Unable to create payment method");
        return;
      }

      await onSavePaymentMethod(paymentMethod.id);
    } catch (err) {
      setError(err?.message || "Failed to save payment method");
    }
  };

  return (
    <div className="px-6 py-6 md:py-6">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div>
          <label className="block text-[#242424] text-sm mb-1">Card Details</label>
          <div className="w-full bg-[#F6F7F9] rounded-md px-3 py-3">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#242424",
                    '::placeholder': { color: "#9ca3af" },
                  },
                  invalid: { color: "#ef4444" },
                },
              }}
              onChange={(e) => {
                setCardComplete(e.complete);
                if (e.error) setError(e.error.message);
                else setError(null);
              }}
            />
          </div>
        </div>
        <div>
          <label className="block text-[#242424] text-sm mb-1">Name on Card</label>
          <input
            type="text"
            value={nameOnCard}
            onChange={(e) => setNameOnCard(e.target.value)}
            placeholder="John Doe"
            className="w-full bg-[#F6F7F9] rounded-md h-11 px-3 outline-none"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="block text-[#242424] text-sm mb-1">Billing Address</label>
            <input
              type="text"
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
              placeholder="123 Main Street"
              className="w-full bg-[#F6F7F9] rounded-md h-11 px-3 outline-none"
            />
          </div>
          <div>
            <label className="block text-[#242424] text-sm mb-1">State</label>
            <select
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              className="w-full bg-[#F6F7F9] rounded-md h-11 px-3 outline-none"
            >
              <option value="" disabled>
                Select
              </option>
              {usStates.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[#242424] text-sm mb-1">ZIP</label>
            <input
              type="text"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder="Enter ZIP code"
              className="w-full bg-[#F6F7F9] rounded-md h-11 px-3 outline-none"
            />
          </div>
        </div>
        {error ? (
          <div className="text-red-600 text-sm">{error}</div>
        ) : null}
        <div className="flex justify-end pt-2 gap-3">
          <button
            type="button"
            className="bg-white text-[#242424] border border-[#D9D9D9] px-5 py-2.5 rounded-md"
            onClick={onBack}
          >
            Back
          </button>
          <button
            type="submit"
            className="bg-[#3cab5c] text-white px-5 py-2.5 rounded-md disabled:opacity-60"
            disabled={!canSubmit}
          >
            Save as Default
          </button>
        </div>
      </form>
    </div>
  );
};

// Inner component responsible for Stripe ACH (US Bank Account) entry and submission
const ACHPaymentForm = ({ onBack, onSavePaymentMethod, saving, customerName, customerEmail }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!stripe || !elements) return;

    // For ACH, Stripe requires confirming the setup to verify the bank
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      return;
    }

    try {
      // This will trigger the bank login flow (Stripe Financial Connections)
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: {
            name: customerName || "Customer Name",
            email: customerEmail,
          },
        },
      });

      if (pmError) {
        setError(pmError.message);
      } else {
        await onSavePaymentMethod(paymentMethod.id);
      }
    } catch (err) {
      console.error("ACH setup error:", err);
      setError("An unexpected error occurred.");
    }
  };

  return (
    <div className="px-6 py-6 md:py-6">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <PaymentElement
          options={{
            layout: "tabs",
            paymentMethodTypes: ["us_bank_account"],
            fields: {
              billingDetails: {
                email: "never",
              },
            },
          }}
        />

        {error && <div className="text-red-600 text-sm">{error}</div>}

        <div className="flex justify-end pt-2 gap-3">
          <button
            type="button"
            className="bg-white text-[#242424] border border-[#D9D9D9] px-5 py-2.5 rounded-md"
            onClick={onBack}
          >
            Back
          </button>
          <button
            type="submit"
            className="bg-[#3cab5c] text-white px-5 py-2.5 rounded-md disabled:opacity-60"
            disabled={!stripe || saving}
          >
            {saving ? "Saving..." : "Link Bank Account"}
          </button>
        </div>
      </form>
    </div>
  );
};

/**
 * PaymentMethodModal
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - onAdd: function (optional) invoked when primary CTA is clicked from the selection screen for non-card flows)
 */
const PaymentMethodModal = ({ open, onClose, onAdd }) => {
  const { userData, setUserData } = useUser();
  const { callApi, loading } = useApiHandler();

  // step: 'SELECT' | 'CARD' | 'BANK'
  const [step, setStep] = useState("SELECT");
  const [paymentType, setPaymentType] = useState("CARD");

  // Card form state (Stripe handles card details via CardElement)
  const [nameOnCard, setNameOnCard] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [zip, setZip] = useState("");

  useEffect(() => {
    if (open) {
      // reset to entry view whenever modal is opened
      setStep("SELECT");
      setPaymentType("CARD");
      // clear previous input
      setNameOnCard("");
      setBillingAddress("");
      setStateCode("");
      setZip("");
    }
  }, [open]);

  const handlePrimaryClick = async () => {
    if (step === "SELECT") {
      if (paymentType === "CARD") {
        setStep("CARD");
        return;
      }
      if (paymentType === "BANK") {
        setStep("BANK");
        return;
      }
    }
  };

  const usStates = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VA","VT","WA","WI","WV","WY"
  ];

  const title = step === "CARD" ? "Add Credit or Debit Card" : step === "BANK" ? "Add Checking Account" : userData?.hasPaymentMethod ? "Add Payment Method" : "Wallet";

  return (
    <GenericModal open={open} onClose={onClose} title={title} fullWidth maxWidth="756px">
      {step === "SELECT" ? (
        <div className="px-6 py-8 md:py-10">
          <div className="flex flex-col items-center text-center gap-4 md:gap-5">
            <img src={PaymentIllustration} alt="Wallet illustration" className="w-[170px] h-[144px]" />
            <h2 className="text-[#242424] text-[20px] md:text-[24px] font-medium">
              {userData?.hasPaymentMethod ? "Add a new payment method" : "You haven't added any payment methods yet."}
            </h2>
            <p className="text-[#242424] text-[14px] md:text-[16px]">
              {userData?.hasPaymentMethod ? "Choose a payment method type to continue." : "Start by adding a default payment method to post your first job."}
            </p>
            <div className="flex items-center gap-8 md:gap-12 pt-2">
              <label className="flex items-center gap-2 text-[#242424] text-[14px] md:text-[16px]">
                <input
                  type="radio"
                  name="paymentType"
                  value="CARD"
                  checked={paymentType === "CARD"}
                  onChange={(e) => setPaymentType(e.target.value)}
                />
                Credit or Debit Card
              </label>
              <label className="flex items-center gap-2 text-[#242424] text-[14px] md:text-[16px]">
                <input
                  type="radio"
                  name="paymentType"
                  value="BANK"
                  checked={paymentType === "BANK"}
                  onChange={(e) => setPaymentType(e.target.value)}
                />
                Checking Account
              </label>
            </div>
            <button
              type="button"
              className="mt-2 bg-[#3cab5c] text-white px-4 md:px-5 py-2 md:py-2.5 rounded-md flex items-center gap-2"
              onClick={handlePrimaryClick}
            >
              <img src={PlusIcon} alt="plus" className="w-4 h-4" />
              {userData?.hasPaymentMethod ? "Add Payment Method" : "Add Default Payment Method"}
            </button>
          </div>
        </div>
      ) : step === "CARD" ? (
        <Elements stripe={stripePromise}>
          <CardPaymentForm
            nameOnCard={nameOnCard}
            setNameOnCard={setNameOnCard}
            billingAddress={billingAddress}
            setBillingAddress={setBillingAddress}
            stateCode={stateCode}
            setStateCode={setStateCode}
            zip={zip}
            setZip={setZip}
            usStates={usStates}
            saving={loading}
            onBack={() => setStep("SELECT")}
            onSavePaymentMethod={async (pmId) => {
              await callApi(
                () => UserApi.saveStripePaymentMethod(pmId),
                {
                  onSuccess: (resp) => {
                    const updated = resp?.data || {};
                    const updatedUser = updated.user || updated;
                    setUserData({ ...(userData || {}), ...updatedUser, hasPaymentMethod: true });
                    onClose?.();
                  },
                }
              );
            }}
          />
        </Elements>
      ) : (
        <Elements
          key={userData?.email || "ach-setup"}
          stripe={stripePromise}
          options={{
            mode: "setup",
            currency: "usd",
            paymentMethodCreation: "manual",
            payment_method_types: ["us_bank_account"],
            defaultValues: {
              billingDetails: {
                name: `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim(),
                email: userData?.email || "",
              },
            },
            fields: {
              billingDetails: {
                email: "never",
              },
            },
          }}
        >
          <ACHPaymentForm
            onBack={() => setStep("SELECT")}
            onSavePaymentMethod={async (pmId) => {
              await callApi(
                () => UserApi.saveStripePaymentMethod(pmId),
                {
                  onSuccess: (resp) => {
                    const updated = resp?.data || {};
                    const updatedUser = updated.user || updated;
                    setUserData({ ...(userData || {}), ...updatedUser, hasPaymentMethod: true });
                    onClose?.();
                  },
                }
              );
            }}
            saving={loading}
            customerName={`${userData?.first_name || ""} ${userData?.last_name || ""}`.trim()}
            customerEmail={userData?.email}
          />
        </Elements>
      )}
    </GenericModal>
  );
};

export default PaymentMethodModal;
